import './app.scss';
import React, {Fragment, useEffect, useRef} from 'react';
import {Navigate, Route, Routes, useLocation, useParams} from 'react-router-dom';
import {Header} from './header/header';
import {SearchPage} from './search/page/searchPage';
import {Spinner} from './common/spinner/spinner';
import {LandingPage} from './landingPage/landingPage';
import {SettingsPage} from './settings/settingsPage';
import {AnalysisPage} from './analysis/analysisPage';
import {useUiConfigRequest} from './config/config-service';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {SEARCH_PATH} from './config';
import {URL_QUERY_PARAM} from './search/const';
import {useSettingsContainerRequest} from './search/search-hooks';
import {GeneralAssistant} from './assistant/general-assistant/generalAssistant';
import {DocumentAssistantPage} from './assistant/general-assistant/documentAssistantPage';
import {LocalizationProvider} from './localizationUtil';
import {HibuUiConfig} from './config/config-types';
import {ModalProvider} from './common/hibuModal/hibuModal';
import {SearchPageType} from './search/search-config';
import {doClosePointInTime} from './services/search-service';
import {clearPointInTimeAction} from './search/duck/search-actions';
import type {Dispatch} from 'redux';

function clearPointInTimeWhenLeavingSearch(
    previousPath: string,
    currentPath: string,
    pointInTime: string | null | undefined,
    landingPageEnabled: boolean,
    searchPageEnabled: boolean,
    dispatch: Dispatch,
): void {
    const wasSearchRoute =
        previousPath.startsWith(SEARCH_PATH) || (previousPath === '/' && !landingPageEnabled && searchPageEnabled);
    const isSearchRouteNow =
        currentPath.startsWith(SEARCH_PATH) || (currentPath === '/' && !landingPageEnabled && searchPageEnabled);

    if (wasSearchRoute && !isSearchRouteNow && pointInTime) {
        dispatch(doClosePointInTime(pointInTime));
        dispatch(clearPointInTimeAction(SearchPageType.BASIC));
    }
}

export const RootLayout = (): React.JSX.Element => {
    useSettingsContainerRequest();
    useUiConfigRequest();

    const location = useLocation();
    const dispatch = useDispatch();
    const uiConfig: HibuUiConfig = useSelector((state: DefaultRootState) => state.config.uiConfig);
    const pointInTime = useSelector((state: DefaultRootState) => state.search[SearchPageType.BASIC].pointInTime);
    const previousPathRef = useRef(location.pathname);

    /*  This ensures that focus styles only appear when tabbing (and not when clicking) */
    useEffect(() => {
        const handleKeyDown = (e: { key: string }) => {
            if (e.key === 'Tab') {
                document.documentElement.classList.add('user-is-tabbing');
            }
        };

        const handleMouseDown = () => {
            document.documentElement.classList.remove('user-is-tabbing');
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('mousedown', handleMouseDown);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    const searchPageEnabled = uiConfig.search.enabled;
    const analysisPageEnabled = uiConfig.analysis.enabled;
    const landingPageEnabled = uiConfig.landingPage.enabled;
    const assistantPageEnabled = uiConfig.aiAssistant?.globalAssistant.enabled;
    const documentAssistantPageEnabled = uiConfig.aiAssistant?.allDocsAssistant.enabled;

    useEffect(() => {
        clearPointInTimeWhenLeavingSearch(
            previousPathRef.current,
            location.pathname,
            pointInTime,
            landingPageEnabled,
            searchPageEnabled,
            dispatch,
        );
        previousPathRef.current = location.pathname;
    }, [dispatch, location.pathname, pointInTime, landingPageEnabled, searchPageEnabled]);

    if (Object.entries(uiConfig.styleVariables).length === 0) {
        return <Spinner/>;
    }

    function getLandingPage() {
        if (landingPageEnabled) {
            return <Route path="/" element={<LandingPage/>}/>;
        } else if (searchPageEnabled) {
            return <Route path="/" element={<SearchPage/>}/>;
        } else if (analysisPageEnabled) {
            return <Route path="/" element={<AnalysisPage/>}/>;
        } else if (assistantPageEnabled) {
            return <Route path="/" element={<GeneralAssistant/>}/>;
        } else if (documentAssistantPageEnabled) {
            return <Route path="/" element={<DocumentAssistantPage/>}/>;
        } else {
            return <div>Error: no route defined!</div>;
        }
    }

    const GetUriWithQueryParams = () => {
        const {query} = useParams(); // Get route params
        const location = useLocation(); // Get current location
        const splitUriOnQuestionMark = location.search.split('?').filter((q) => q !== '');

        let queryString: string = query ? query : '';
        let queryParameters = '';

        if (splitUriOnQuestionMark.length > 1) {
            queryString += `?${splitUriOnQuestionMark.slice(0, -1).join('?')}`;
            queryParameters = `${splitUriOnQuestionMark[splitUriOnQuestionMark.length - 1]}`;
        }

        const combinedQueryParameters = new URLSearchParams({
            [URL_QUERY_PARAM]: queryString,
            ...Object.fromEntries(new URLSearchParams(`?${queryParameters}`)),
        });

        return <Navigate to={`${SEARCH_PATH}?${combinedQueryParameters.toString()}`}/>;
    };

    return (
        <LocalizationProvider>
            <ModalProvider>
                <Fragment>
                    <Header/>
                    <div id="content">
                        <Routes>
                            <Route path={SEARCH_PATH + '/:query'} element={<GetUriWithQueryParams/>}/>
                            <Route path={SEARCH_PATH} element={<SearchPage/>}/>
                            <Route path="/analysis" element={<AnalysisPage/>}/>
                            <Route path="/settings" element={<SettingsPage/>}/>
                            <Route path="/assistant" element={<GeneralAssistant/>}/>
                            <Route path="/document-assistant" element={<DocumentAssistantPage/>}/>
                            {getLandingPage()}
                        </Routes>
                    </div>
                </Fragment>
            </ModalProvider>
        </LocalizationProvider>
    );
};
